import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="photo-upload"
export default class extends Controller {
  static targets = [
    "input",
    "preview",
    "dropZone",
    "dragIndicator",
    "currentCount",
    "requiredCount",
    "uploadProgress",
    "uploadCount",
    "uploadStatus",
    "progressBar",
    "compressionNotice"
  ]
  static outlets = ["order-form"]

  static values = {
    allowedTypes: { type: Array, default: ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif'] },
    allowedExtensions: { type: Array, default: ['.jpg', '.jpeg', '.png', '.heic', '.heif'] },
    maxSize: { type: Number, default: 10485760 } // 10MB in bytes
  }

  connect() {
    this.selectedFiles = []
    this.maxFiles = 6
    this.preventDefaultDrag()
  }

  preventDefaultDrag() {
    document.addEventListener('dragover', (e) => e.preventDefault())
    document.addEventListener('drop', (e) => e.preventDefault())
  }

  // Called by order-form controller when required count changes
  updateRequiredCount(count) {
    this.maxFiles = count
    this.requiredCountTarget.textContent = count
    this.updatePhotoCount()
  }

  updatePhotoCount() {
    this.currentCountTarget.textContent = this.selectedFiles.length
  }

  getFileCount() {
    return this.selectedFiles.length
  }

  handleInputChange(event) {
    this.handleFileSelection(Array.from(event.target.files))
  }

  handleDragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.add('border-indigo-400', 'bg-indigo-50')
    this.dragIndicatorTarget.classList.remove('hidden')
  }

  handleDragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.remove('border-indigo-400', 'bg-indigo-50')
    this.dragIndicatorTarget.classList.add('hidden')
  }

  handleDrop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.remove('border-indigo-400', 'bg-indigo-50')
    this.dragIndicatorTarget.classList.add('hidden')

    const files = Array.from(event.dataTransfer.files)
    const imageFiles = this.filterAllowedFiles(files)

    if (imageFiles.length !== files.length) {
      alert('Only JPG, JPEG, PNG, or HEIC files are allowed')
      return
    }

    if (!this.validateFileSizes(imageFiles)) {
      return
    }

    this.handleFileSelection(imageFiles)
  }

  filterAllowedFiles(files) {
    return files.filter(file => {
      const hasAllowedType = this.allowedTypesValue.includes(file.type)
      const hasAllowedExtension = this.allowedExtensionsValue.some(ext =>
        file.name.toLowerCase().endsWith(ext)
      )
      return hasAllowedType || hasAllowedExtension
    })
  }

  validateFileSizes(files) {
    const SANITY_LIMIT = 50 * 1024 * 1024 // 50MB — truly unworkable files only
    const oversizedFiles = files.filter(file => file.size > SANITY_LIMIT)
    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map(f => f.name).join(', ')
      alert(`The following files are too large to upload (over 50MB): ${fileNames}`)
      return false
    }
    return true
  }

  async compressIfNeeded(file) {
    const MAX_SIZE = 4 * 1024 * 1024 // 4MB
    const MAX_DIM = 3000
    const QUALITY = 0.90

    let workingFile = file
    const isHeic = file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')

    if (isHeic) {
      try {
        const { heicTo } = await import("heic-to")
        const jpegBlob = await heicTo({ blob: file, toType: 'image/jpeg', quality: QUALITY })
        const newName = file.name.replace(/\.[^.]+$/, '.jpg')
        workingFile = new File([jpegBlob], newName, { type: 'image/jpeg', lastModified: Date.now() })
      } catch (e) {
        // If conversion fails, pass through original and let server handle it
        console.warn('HEIC conversion failed, uploading original:', e)
        return file
      }
    }

    if (workingFile.size <= MAX_SIZE) return workingFile

    return new Promise((resolve) => {
      const img = new Image()
      const url = URL.createObjectURL(workingFile)
      img.onload = () => {
        URL.revokeObjectURL(url)
        let { width, height } = img
        if (width > MAX_DIM || height > MAX_DIM) {
          if (width >= height) {
            height = Math.round((height / width) * MAX_DIM)
            width = MAX_DIM
          } else {
            width = Math.round((width / height) * MAX_DIM)
            height = MAX_DIM
          }
        }
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        canvas.getContext('2d').drawImage(img, 0, 0, width, height)
        canvas.toBlob((blob) => {
          if (blob && blob.size < workingFile.size) {
            const newName = workingFile.name.replace(/\.[^.]+$/, '.jpg')
            resolve(new File([blob], newName, { type: 'image/jpeg', lastModified: Date.now() }))
          } else {
            resolve(workingFile)
          }
        }, 'image/jpeg', QUALITY)
      }
      img.onerror = () => { URL.revokeObjectURL(url); resolve(workingFile) }
      img.src = url
    })
  }

  async compressFiles(files) {
    const compressedFiles = await Promise.all(files.map(f => this.compressIfNeeded(f)))
    const anyCompressed = compressedFiles.some((f, i) => f !== files[i])
    return { compressedFiles, anyCompressed }
  }

  showCompressionNotice() {
    if (!this.hasCompressionNoticeTarget) return
    this.compressionNoticeTarget.classList.remove('hidden')
    setTimeout(() => {
      this.compressionNoticeTarget.classList.add('hidden')
    }, 4000)
  }

  async handleFileSelection(files) {
    const selectedSize = document.querySelector('input[name="order[magnet_size]"]:checked')

    if (!selectedSize) {
      alert('Please select a magnet size first')
      this.inputTarget.value = ''
      return
    }

    const maxFiles = this.maxFiles
    const availableSlots = maxFiles - this.selectedFiles.length

    if (files.length > availableSlots) {
      if (availableSlots === 0) {
        alert(`You already have ${maxFiles} photos selected. Remove some photos to add more.`)
      } else {
        alert(`You can only add ${availableSlots} more photo(s). You need exactly ${maxFiles} photos total.`)
      }
      this.inputTarget.value = ''
      return
    }

    const { compressedFiles, anyCompressed } = await this.compressFiles(files)
    if (anyCompressed) this.showCompressionNotice()

    this.selectedFiles = [...this.selectedFiles, ...compressedFiles]
    this.updateFileInput()
    this.showUploadProgress(compressedFiles.length)
    this.processFilesWithProgress(compressedFiles)
  }

  updateFileInput() {
    const dt = new DataTransfer()
    this.selectedFiles.forEach(file => dt.items.add(file))
    this.inputTarget.files = dt.files
  }

  showUploadProgress(totalFiles) {
    this.uploadProgressTarget.classList.remove('hidden')
    this.uploadCountTarget.textContent = `0 of ${totalFiles}`
    this.progressBarTarget.style.width = '0%'
  }

  updateProgress(current, total) {
    const percentage = Math.round((current / total) * 100)

    this.uploadCountTarget.textContent = `${current} of ${total}`
    this.progressBarTarget.style.width = `${percentage}%`

    if (current === total) {
      this.uploadStatusTarget.textContent = 'Photos ready!'
      setTimeout(() => {
        this.uploadProgressTarget.classList.add('hidden')
      }, 1000)
    }
  }

  processFilesWithProgress(files) {
    let processedCount = 0
    const totalFiles = files.length

    files.forEach((file, index) => {
      setTimeout(() => {
        processedCount++
        this.updateProgress(processedCount, totalFiles)

        if (processedCount === totalFiles) {
          this.displayPhotoPreview(this.selectedFiles)
          this.notifyFormController()
        }
      }, (index + 1) * 200)
    })
  }

  displayPhotoPreview(files) {
    this.previewTarget.innerHTML = ''

    if (files.length === 0) {
      this.previewTarget.classList.add('hidden')
      this.updatePhotoCount()
      this.notifyFormController()
      return
    }

    this.previewTarget.classList.remove('hidden')

    files.forEach((file, index) => {
      const isHeic = file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')

      const div = document.createElement('div')
      div.className = 'relative group'
      div.dataset.index = index

      if (isHeic) {
        // Show placeholder for HEIC files since browsers can't display them directly
        div.innerHTML = `
          <div class="w-full h-24 bg-gray-200 rounded-md flex flex-col items-center justify-center">
            <svg class="w-8 h-8 text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
            </svg>
            <span class="text-xs text-gray-500">HEIC</span>
          </div>
          <div class="absolute top-1 right-1 bg-black bg-opacity-50 text-white text-xs px-1 rounded">${index + 1}</div>
          <button type="button"
                  class="remove-photo absolute top-1 left-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                  data-photo-upload-index-param="${index}"
                  data-action="click->photo-upload#removePhoto"
                  title="Remove photo">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        `
        this.previewTarget.appendChild(div)
      } else {
        // Show actual preview for JPG, PNG files
        const reader = new FileReader()
        reader.onload = (e) => {
          div.innerHTML = `
            <img src="${e.target.result}" alt="Photo ${index + 1}" class="w-full h-24 object-cover rounded-md">
            <div class="absolute top-1 right-1 bg-black bg-opacity-50 text-white text-xs px-1 rounded">${index + 1}</div>
            <button type="button"
                    class="remove-photo absolute top-1 left-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                    data-photo-upload-index-param="${index}"
                    data-action="click->photo-upload#removePhoto"
                    title="Remove photo">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          `
          this.previewTarget.appendChild(div)
        }
        reader.readAsDataURL(file)
      }
    })

    this.updatePhotoCount()
  }

  removePhoto(event) {
    event.preventDefault()
    const index = parseInt(event.params.index)

    // Remove from files array
    this.selectedFiles.splice(index, 1)
    this.updateFileInput()

    // Remove the photo element from DOM directly (no flicker)
    const photoElements = this.previewTarget.querySelectorAll('[data-index]')
    if (photoElements[index]) {
      photoElements[index].remove()
    }

    // Re-index remaining photos
    const remainingElements = this.previewTarget.querySelectorAll('[data-index]')
    remainingElements.forEach((element, newIndex) => {
      element.dataset.index = newIndex

      // Update the photo number badge
      const badge = element.querySelector('.absolute.top-1.right-1')
      if (badge) {
        badge.textContent = newIndex + 1
      }

      // Update the remove button's data attribute
      const removeBtn = element.querySelector('.remove-photo')
      if (removeBtn) {
        removeBtn.dataset.photoUploadIndexParam = newIndex
      }
    })

    // Hide preview if no photos left
    if (this.selectedFiles.length === 0) {
      this.previewTarget.classList.add('hidden')
    }

    this.updatePhotoCount()
    this.notifyFormController()
  }

  notifyFormController() {
    if (this.hasOrderFormOutlet) {
      this.orderFormOutlet.checkFormValidity()
    }
  }
}
